/**
 * @fileoverview Cross-domain authentication handoff utilities
 * @module utils/authHandoff
 *
 * Handles seamless authentication when redirecting users between domains
 * (e.g., from planthra.com to ejb711.planthra.com).
 *
 * Flow:
 * 1. On source domain: generate a custom token via Cloud Function
 * 2. Redirect to target domain with token in URL parameter
 * 3. On target domain: consume token and sign in with signInWithCustomToken
 */
import { httpsCallable } from 'firebase/functions'
import { signInWithCustomToken } from 'firebase/auth'
import { functions, auth } from '@/plugins/firebase'

const AUTH_TOKEN_PARAM = 'authToken'

/**
 * Generate a custom token for cross-domain auth handoff.
 * Call this before redirecting to another domain.
 *
 * @returns The custom token string
 */
export async function generateHandoffToken(): Promise<string> {
  const generateToken = httpsCallable<void, { token: string }>(
    functions,
    'generateAuthHandoffToken'
  )
  const result = await generateToken()
  return result.data.token
}

/**
 * Redirect to a URL with the auth handoff token appended.
 * Generates a token and includes it as a URL parameter.
 *
 * @param targetUrl - The URL to redirect to
 */
export async function redirectWithAuthHandoff(targetUrl: string): Promise<void> {
  try {
    const token = await generateHandoffToken()
    const url = new URL(targetUrl)
    url.searchParams.set(AUTH_TOKEN_PARAM, token)
    window.location.href = url.toString()
  } catch (error) {
    console.error('Failed to generate auth handoff token:', error)
    // Fall back to redirect without token - user will need to sign in again
    window.location.href = targetUrl
  }
}

/**
 * Check if there's an auth handoff token in the URL and consume it.
 * Should be called early in app initialization.
 *
 * @returns true if a token was found and consumed, false otherwise
 */
export async function consumeAuthHandoffToken(): Promise<boolean> {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get(AUTH_TOKEN_PARAM)

  if (!token) {
    return false
  }

  try {
    // Sign in with the custom token
    await signInWithCustomToken(auth, token)

    // Remove the token from the URL to prevent reuse
    urlParams.delete(AUTH_TOKEN_PARAM)
    const newUrl = new URL(window.location.href)
    newUrl.search = urlParams.toString()
    window.history.replaceState({}, '', newUrl.toString())

    return true
  } catch (error) {
    console.error('Failed to consume auth handoff token:', error)
    // Remove invalid token from URL
    urlParams.delete(AUTH_TOKEN_PARAM)
    const newUrl = new URL(window.location.href)
    newUrl.search = urlParams.toString()
    window.history.replaceState({}, '', newUrl.toString())
    return false
  }
}
